import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';

import { addNote } from '../../api/manualReenrolment';
import { colour, primaryButtonSx } from '../admin/tokens';
import { commentDate } from '../../utils/familyComments';

/**
 * Comments on this family, from AR and the enrolment team: every one, oldest first, and a box to add
 * another.
 *
 * Who wrote it comes from the sign-in, not from anything typed. A comment is not edited or deleted —
 * the list reads as what happened, and a correction is a further comment. View-only access reads but
 * does not add (the server refuses it too).
 */
const FamilyComments = ({ sessionId, comments = [], canAdd = true, onChange }) => {
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const add = async () => {
        if (!draft.trim()) return;
        setSaving(true);
        setError('');
        try {
            const updated = await addNote({ SessionID: sessionId, note: draft.trim() });
            setDraft('');
            onChange?.(updated);
        } catch (failure) {
            setError(failure?.response?.data?.error || 'Could not save that comment. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: colour.body, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.25 }}>
                Comments
            </Typography>

            {comments.length === 0 && (
                <Typography sx={{ fontSize: '13.5px', color: colour.faint, mb: 1.5 }}>No comments yet.</Typography>
            )}
            {comments.map((comment) => (
                <Box key={comment.id} data-comment sx={{ borderLeft: `3px solid ${colour.border}`, pl: 1.5, mb: 1.25 }}>
                    <Typography sx={{ fontSize: '14px', color: colour.body, whiteSpace: 'pre-wrap' }}>{comment.note}</Typography>
                    <Typography sx={{ fontSize: '12px', color: colour.faint }}>
                        {`${comment.addedBy || ''} · ${commentDate(comment.addedAt)}`}
                    </Typography>
                </Box>
            ))}

            {error && <Alert severity="error" sx={{ my: 1, borderRadius: '12px' }}>{error}</Alert>}

            {canAdd && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={6}
                        placeholder="Add a comment for AR and the enrolment team"
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        inputProps={{ 'aria-label': 'New comment', maxLength: 2000 }}
                    />
                    <Button
                        variant="contained"
                        disabled={saving || !draft.trim()}
                        onClick={add}
                        sx={{ ...primaryButtonSx, whiteSpace: 'nowrap' }}
                    >
                        {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Add comment'}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default FamilyComments;
